import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
} from "@mui/material";
import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { formatDuration } from "./dashboardFormatters";
import type { ProcessDurationStatistic } from "../../api/bpm/processDashboard";

type SortColumn = "name" | "averageDuration" | "completedExecutions" | "executions";
type SortOrder = "asc" | "desc";

interface ProcessDurationDialogProps {
    open: boolean;
    processes: ProcessDurationStatistic[];
    onClose: () => void;
}

const getProcessName = (process: ProcessDurationStatistic, fallback: string) =>
    process.processDefinitionName || process.processDefinitionKey || fallback;

export default function ProcessDurationDialog({
    open,
    processes,
    onClose,
}: ProcessDurationDialogProps) {
    const { t } = useTranslation();
    const [search, setSearch] = useState("");
    const [sortColumn, setSortColumn] = useState<SortColumn>("averageDuration");
    const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const unnamedProcess = t("dashboard.processMetrics.unnamedProcess");

    const filteredProcesses = useMemo(() => {
        const normalizedSearch = search.trim().toLocaleLowerCase();
        const filtered = normalizedSearch
            ? processes.filter((process) =>
                getProcessName(process, unnamedProcess)
                    .toLocaleLowerCase()
                    .includes(normalizedSearch),
            )
            : processes;

        return [...filtered].sort((left, right) => {
            let comparison: number;
            if (sortColumn === "name") {
                comparison = getProcessName(left, unnamedProcess).localeCompare(
                    getProcessName(right, unnamedProcess),
                );
            } else if (sortColumn === "averageDuration") {
                comparison = (left.averageDurationInMillis ?? 0) -
                    (right.averageDurationInMillis ?? 0);
            } else if (sortColumn === "completedExecutions") {
                comparison = (left.completedExecutions ?? 0) - (right.completedExecutions ?? 0);
            } else {
                comparison = (left.executions ?? 0) - (right.executions ?? 0);
            }
            return sortOrder === "asc" ? comparison : -comparison;
        });
    }, [processes, search, sortColumn, sortOrder, unnamedProcess]);

    const currentPage = Math.min(
        page,
        Math.max(0, Math.ceil(filteredProcesses.length / rowsPerPage) - 1),
    );
    const visibleProcesses = filteredProcesses.slice(
        currentPage * rowsPerPage,
        currentPage * rowsPerPage + rowsPerPage,
    );

    const handleSort = (column: SortColumn) => {
        if (column === sortColumn) {
            setSortOrder((current) => current === "asc" ? "desc" : "asc");
        } else {
            setSortColumn(column);
            setSortOrder(column === "name" ? "asc" : "desc");
        }
        setPage(0);
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>{t("dashboard.processMetrics.processDurationListTitle")}</DialogTitle>
            <DialogContent>
                <TextField
                    fullWidth
                    size="small"
                    label={t("dashboard.processMetrics.searchProcesses")}
                    value={search}
                    onChange={(event) => {
                        setSearch(event.target.value);
                        setPage(0);
                    }}
                    sx={{ mt: 1, mb: 2 }}
                />

                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <SortableHeader
                                    column="name"
                                    activeColumn={sortColumn}
                                    order={sortOrder}
                                    onSort={handleSort}
                                >
                                    {t("dashboard.processMetrics.processColumn")}
                                </SortableHeader>
                                <SortableHeader
                                    column="averageDuration"
                                    activeColumn={sortColumn}
                                    order={sortOrder}
                                    onSort={handleSort}
                                    align="right"
                                >
                                    {t("dashboard.processMetrics.averageDurationColumn")}
                                </SortableHeader>
                                <SortableHeader
                                    column="completedExecutions"
                                    activeColumn={sortColumn}
                                    order={sortOrder}
                                    onSort={handleSort}
                                    align="right"
                                >
                                    {t("dashboard.processMetrics.completionsColumn")}
                                </SortableHeader>
                                <SortableHeader
                                    column="executions"
                                    activeColumn={sortColumn}
                                    order={sortOrder}
                                    onSort={handleSort}
                                    align="right"
                                >
                                    {t("dashboard.processMetrics.executionsColumn")}
                                </SortableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleProcesses.map((process) => (
                                <TableRow
                                    key={`${process.processDefinitionKey}-${process.processDefinitionName}`}
                                >
                                    <TableCell>{getProcessName(process, unnamedProcess)}</TableCell>
                                    <TableCell align="right">
                                        {formatDuration(process.averageDurationInMillis)}
                                    </TableCell>
                                    <TableCell align="right">
                                        {process.completedExecutions ?? 0}
                                    </TableCell>
                                    <TableCell align="right">{process.executions ?? 0}</TableCell>
                                </TableRow>
                            ))}
                            {!visibleProcesses.length ? (
                                <TableRow>
                                    <TableCell colSpan={4}>
                                        <Typography color="text.secondary" textAlign="center" py={3}>
                                            {t("dashboard.processMetrics.noMatchingProcesses")}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : null}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={filteredProcesses.length}
                    page={currentPage}
                    onPageChange={(_, nextPage) => setPage(nextPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(event) => {
                        setRowsPerPage(Number(event.target.value));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[5, 10, 25]}
                    labelRowsPerPage={t("dashboard.processMetrics.rowsPerPage")}
                    labelDisplayedRows={({ from, to, count }) =>
                        t("dashboard.processMetrics.displayedRows", { from, to, count })
                    }
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t("common.close")}</Button>
            </DialogActions>
        </Dialog>
    );
}

function SortableHeader({
    column,
    activeColumn,
    order,
    onSort,
    align,
    children,
}: {
    column: SortColumn;
    activeColumn: SortColumn;
    order: SortOrder;
    onSort: (column: SortColumn) => void;
    align?: "left" | "right";
    children: ReactNode;
}) {
    return (
        <TableCell align={align} sortDirection={activeColumn === column ? order : false}>
            <TableSortLabel
                active={activeColumn === column}
                direction={activeColumn === column ? order : "asc"}
                onClick={() => onSort(column)}
            >
                {children}
            </TableSortLabel>
        </TableCell>
    );
}
